import Image from 'next/image';

import { breakpoint } from '@carvana/helios-styles';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Logo from '../../Logo';
import useLayoutSize from '../../../hooks/useLayoutSize';
import { getVehicleImage, getVehicleMake } from '../campaignMapping';
import useWindowSize from './useWindowSize';
import type { Treatments } from './SpecialTreatment';
import SpecialTreatment from './SpecialTreatment';

import {
  Background,
  Card,
  CardWrapper,
  LogoWrapper,
  Heading,
  HeadingWrapper,
  HeroContainer,
  ImageWrapper,
  ImageContainer,
  Subheading,
  VectorWrap,
  VehicleImageWrapper
} from './Hero.styles';
import VehicleEntry from './VehicleEntry';

const getDynamicViewbox = (
  width: number,
  isXs: boolean,
  isSm: boolean,
  isMd: boolean,
  isXl: boolean
) => {
  const breakpointValues = Object.entries(breakpoint).reduce((acc, [key, value]) => {
    acc[key] = parseFloat(value.replace('px', ''));
    return acc;
  }, {}) as { [key: string]: number };
  const mobileBreakpoint = 375;

  const smAspectRatio = 0.34;
  const mdAspectRatio = 0.39;
  const xlAspectRatio = 0.29;
  const smAspectFactor = 0.9;
  const mdAspectFactor = 0.5;
  const xlAspectFactor = 0.5;

  if (isXs && !isSm) {
    return `${((width - mobileBreakpoint) / 2) * -1} 0 ${mobileBreakpoint} 188`;
  }

  if (isSm && !isMd) {
    return `${((width - (breakpointValues.sm - 440)) / 3) * -1} 0 ${
      breakpointValues.sm * smAspectFactor
    } ${breakpointValues.sm * smAspectFactor * smAspectRatio}`;
  }

  if (isMd && !isXl) {
    return `${((width - (breakpointValues.md - 600)) / 3) * -1} 0 ${
      breakpointValues.md * mdAspectFactor
    } ${breakpointValues.md * mdAspectFactor * mdAspectRatio}`;
  }

  const rightModifier = 781 + 1800 - width;

  const res = `${
    width >= 1800 ? -285 : ((width - (breakpointValues.xl - rightModifier)) / 4) * -1
  } 0 ${breakpointValues.xl * xlAspectFactor} ${
    breakpointValues.xl * xlAspectFactor * xlAspectRatio
  }`;

  return res;
};

const getHeight = (isXs: boolean, isSm: boolean, isMd: boolean, isXl: boolean) => {
  if (isXs && !isSm) {
    return 188;
  }

  if (isSm && !isMd) {
    return 259;
  }

  if (isMd && !isXl) {
    return 322;
  }

  return 600;
};

const getPreserveMode = (isSm: boolean, isLegacySm: boolean, isMd: boolean, isXl: boolean) => {
  if (isSm && !isMd) {
    return isLegacySm ? 'xMidYMin meet' : 'xMidYMin slice';
  }

  if (isMd && !isXl) {
    return 'xMidYMin meet';
  }

  return isXl ? 'xMidYMid slice' : 'xMidYMid meet';
};

const Vector = ({
  gradientId,
  treatment = 'default'
}: {
  gradientId: string;
  treatment?: Treatments;
}) => {
  const { isXs, isSm, isLegacySm, isMd, isXl } = useLayoutSize();
  const [width] = useWindowSize();
  const dynamicViewbox = getDynamicViewbox(width, isXs, isSm, isMd, isXl);
  const height = getHeight(isXs, isSm, isMd, isXl);
  const preserveMode = getPreserveMode(isSm, isLegacySm, isMd, isXl);
  const isMobile = isXs && !isSm;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height={height}
      viewBox={dynamicViewbox}
      preserveAspectRatio={preserveMode}
      style={{ overflow: 'visible', overflowX: 'clip' }}
      fill="none"
    >
      <SpecialTreatment treatment={treatment} isMobile={isMobile} isXl={isXl} isMd={isMd} />
      <path
        d="M417.388 21.2451H312.177C304.201 21.2451 296.898 25.7161 293.271 32.8198L224.29 167.914C216.783 182.615 196.105 183.5 187.371 169.492L126.942 72.5856C119.374 60.4489 102.214 59.1174 92.8641 69.9414L61.302 106.481C57.2696 111.149 51.4058 113.833 45.237 113.833H-900"
        stroke="#228BE6"
        strokeWidth="40.6879"
        strokeLinecap="square"
      />
      <path
        d="M417.388 21.2451H312.177C304.201 21.2451 296.898 25.7161 293.271 32.8198L224.29 167.914C216.783 182.615 196.105 183.5 187.371 169.492L126.942 72.5856C119.374 60.4489 102.214 59.1174 92.8641 69.9414L61.302 106.481C57.2696 111.149 51.4058 113.833 45.237 113.833H-900"
        stroke={`url(#${gradientId})`}
        strokeWidth="40.6879"
        strokeLinecap="square"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="557.138"
          y1="-32.4339"
          x2="-188.194"
          y2="132.51"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0490212" stopColor="#FFD53D" />
          <stop offset="0.19376" stopColor="#D8F5A2" />
          <stop offset="0.255425" stopColor="#96F5B6" />
          <stop offset="0.404059" stopColor="#3BC9DB" />
          <stop offset="0.663377" stopColor="#228BE6" stopOpacity="0.5" />
          <stop offset="0.924866" stopColor="#228BE6" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const getCampaignId = (matches: string | string[]) => {
  if (!matches) {
    return undefined;
  }

  return typeof matches === 'string' ? matches : matches[0];
};

const imageLoader = ({ src, width, quality }) => `${src}&w=${width}&q=${quality || 75}`;

const getHeadingAndSubheading = (isDynamicHeader: boolean, vehicleMake?: string) => {
  if (isDynamicHeader) {
    return {
      heading: `Stay Up to Speed with Your ${vehicleMake || 'Car'}’s Value`,
      subheading: ''
    };
  }

  return {
    heading: 'Stay Up to Speed with Your Car’s Value',
    subheading: ''
  };
};

const getTreatment = (queryParams: { [key: string]: string | string[] }) => {
  const ppaMode = queryParams?.utm_campaign === 'ppa_event_cvt_signup';
  const chicagoMode = queryParams?.utm_campaign === 'chicago_fire_event_cvt_signup';

  if (ppaMode) {
    return 'ppa';
  }

  return chicagoMode ? 'chicago' : 'default';
};

const Hero = ({ dynamicHeaderBucket = 'control' }: { dynamicHeaderBucket: string }) => {
  const router = useRouter();
  const queryParams = router?.query;
  const isDynamicHeader = dynamicHeaderBucket === 'experiment';
  const campaignId = isDynamicHeader ? getCampaignId(queryParams?.utm_content) : 'control';

  const imageSrc = getVehicleImage(campaignId);
  const vehicleMake = getVehicleMake(campaignId);

  const { heading, subheading } = getHeadingAndSubheading(isDynamicHeader, vehicleMake);

  const treatment = getTreatment(queryParams) as Treatments;

  return (
    <Background className={classNames({ [treatment]: treatment })}>
      <VectorWrap>
        <Vector gradientId="big" treatment={treatment} />
      </VectorWrap>
      <HeroContainer>
        <LogoWrapper>
          <Logo />
        </LogoWrapper>
        <HeadingWrapper>
          <Heading>{heading}</Heading>
          <Subheading>{subheading}</Subheading>
        </HeadingWrapper>
        <ImageContainer>
          <ImageWrapper>
            <Vector gradientId="small" treatment={treatment} />
          </ImageWrapper>
        </ImageContainer>
        <VehicleImageWrapper>
          <Image loader={imageLoader} src={imageSrc} fill alt="vehicle image" />
        </VehicleImageWrapper>
        <CardWrapper>
          <Card>
            <VehicleEntry />
          </Card>
        </CardWrapper>
      </HeroContainer>
    </Background>
  );
};

export default Hero;
